/**
 * createSurfaceApp — MIMARI.md'nin ortak on-yukleme noktasi (FW-14).
 *
 * Bes yuzeyin (auth/app/console/portal/support) main.ts'i BASKA HICBIR SEY
 * KURMAZ: router + i18n + TanStack Query + kanonik api istemcisi burada,
 * TEK fonksiyonda kurulur. Yigin bagimliliklari (vue-router, vue-i18n,
 * @tanstack/vue-query) SURUM olarak bu paketin kendi package.json'inda
 * yasar — yuzeyler onlari DOGRUDAN bagimlilik olarak LISTELEMEZ (bkz.
 * FW-14 KAPSAM B, kabul kriteri 2).
 *
 * COZUMLEME NOTU: pnpm workspace'inde `shamefully-hoist=true` (.npmrc) bir
 * paketin bagimliliklarini SIBLING paketlere otomatik acmaz — yalniz o
 * paketin KENDI transitive agacini duzler (olculdu: root node_modules'te
 * vue bile yok). apps/* Node/Vite modul cozumlemesiyle kendi ata
 * dizinlerinde arar, packages/shell/node_modules'e sicramaz.
 *
 * COZUM (FW-14 duzeltme turu 2/2): yuzeyler bu paketleri DOGRUDAN import
 * ETMEZ — ihtiyac duyduklari API (useI18n, RouteRecordRaw, useRouter...)
 * packages/shell/src/index.ts'ten yeniden disa verilir ve yuzeyler zaten
 * BILDIRDIKLERI @fusapp-work/shell uzerinden alir. Boylece kok package.json'a
 * kopya bagimlilik gerekmez ve kanonik surum TEK noktada (bu paket) kalir.
 *
 * NOT: eskiden bu uc paket kok devDependencies'te de duruyordu; o kopya
 * kaldirildi (olculdu: kok node_modules'te artik ucu de YOK, buna ragmen
 * build ve typecheck exit 0).
 *
 * i18n: `legacy: false` (Composition API modu — bkz. frontend-vue-i18n
 * skill'i). TR varsayilan, EN yedek locale; her ikisi de erkenden yuklenir
 * (bu depoda su an yalniz 2 kucuk JSON locale'i var, lazy-loading'in
 * cozdugu "N locale x 50KB" sorunu burada yok — tercih tarayici depolamasina
 * hic YAZILMAZ, bkz. FW-14 kabul kriteri 3).
 */
import { createApp, type App, type Component } from 'vue'
import {
  createRouter,
  createWebHistory,
  type Router,
  type RouteRecordRaw,
  type RouterHistory,
} from 'vue-router'
import { createI18n, type DefineLocaleMessage } from 'vue-i18n'
import { VueQueryPlugin, QueryClient } from '@tanstack/vue-query'
import { createApiClient, type ApiClient, type ApiClientOptions } from '@fusapp-work/api'
import type { SurfaceName } from './surfaces'
import { API_CLIENT_KEY, IDENTITY_API_CLIENT_KEY } from './apiClient'

export interface SurfaceLocaleMessages {
  readonly tr: DefineLocaleMessage
  readonly en: DefineLocaleMessage
}

export interface CreateSurfaceAppOptions {
  readonly surface: SurfaceName
  readonly rootComponent: Component
  readonly routes: readonly RouteRecordRaw[]
  readonly messages: SurfaceLocaleMessages
  /** Yuzeyin kendi web API'sine (BFF) konusan kanonik istemci — `useApiClient()`. */
  readonly apiClientOptions: ApiClientOptions
  /**
   * FW-142 — opsiyonel IKINCI istemci: identity host'una konusan anonim
   * cagrilar icin (bkz. apiClient.ts basindaki "IKINCI ISTEMCI" notu).
   * Yalniz saglanirsa kurulur ve `useIdentityApiClient()` ile okunur;
   * verilmezse o hook cagrildiginda ayni `useApiClient()` deseniyle
   * aciklayici bir hata firlatir.
   */
  readonly identityApiClientOptions?: ApiClientOptions
  /** Testte gercek `createWebHistory` yerine `createMemoryHistory` verilebilir. */
  readonly history?: RouterHistory
}

export interface SurfaceApp {
  readonly app: App
  readonly router: Router
  readonly queryClient: QueryClient
  readonly apiClient: ApiClient
  readonly identityApiClient?: ApiClient
  mount(selector: string | Element): void
}

export function createSurfaceApp(options: CreateSurfaceAppOptions): SurfaceApp {
  const app = createApp(options.rootComponent)

  const router = createRouter({
    history: options.history ?? createWebHistory(),
    routes: options.routes as RouteRecordRaw[],
  })

  const i18n = createI18n({
    legacy: false,
    locale: 'tr',
    fallbackLocale: 'en',
    messages: {
      tr: options.messages.tr,
      en: options.messages.en,
    },
  })

  const queryClient = new QueryClient()
  const apiClient = createApiClient(options.apiClientOptions)
  const identityApiClient = options.identityApiClientOptions
    ? createApiClient(options.identityApiClientOptions)
    : undefined

  app.use(router)
  app.use(i18n)
  app.use(VueQueryPlugin, { queryClient })
  // FW-126 — bilesenler/composable'lar apiClient'a useApiClient() ile ulasir
  // (bkz. apiClient.ts basindaki gerekce: FW-14 bu asamayi atlamisti).
  app.provide(API_CLIENT_KEY, apiClient)
  // FW-142 — yalniz saglanirsa provide edilir (bkz. apiClient.ts "IKINCI ISTEMCI").
  if (identityApiClient) {
    app.provide(IDENTITY_API_CLIENT_KEY, identityApiClient)
  }

  return {
    app,
    router,
    queryClient,
    apiClient,
    identityApiClient,
    mount(selector: string | Element) {
      app.mount(selector)
    },
  }
}
